import {
  assistantErrorCode,
  assistantErrorStatus,
  schedulingAssistantApi,
} from './api';
import {
  assistantOperationKey,
  assistantOperationsGeneration,
  PAGE_LOAD_ID,
  readAssistantOperations,
  writeAssistantOperation,
} from './operationRecords';

import type { AssistantOperationResult } from './api';
import type {
  AssistantOperationRecord,
  AssistantOperationRecordBase,
} from './operationRecords';
import type { AppDispatch } from '@/store';

// Drives one record to an answer: sends (or replays) a `submitting` record's
// bytes, then polls a `pending` one. Every answer is written to the record,
// not to component state, so it lands even if the page that started the send
// has gone, and a cleared record (sign-out, 401) is never written back.

const DEFAULT_POLL_SECONDS = 2;
const MAX_POLL_SECONDS = 30;

/**
 * Commands being driven in this page: one send or poll loop each, with the
 * records generation it started in. A loop from before a sign-out or 401
 * (both clear every record) stops at its next step and never holds up a
 * newer record for the same appointment.
 */
const active = new Map<string, number>();

const sleep = (seconds: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, seconds * 1000);
  });

const pollDelay = (retryAfterSeconds: number | null, failures: number) =>
  Math.min(
    Math.max(retryAfterSeconds ?? DEFAULT_POLL_SECONDS, 1) * 2 ** failures,
    MAX_POLL_SECONDS,
  );

/** The stored record, if it is still this command (same idempotency key). */
const current = (
  record: AssistantOperationRecord,
): AssistantOperationRecord | null =>
  readAssistantOperations(record.memberId).find(
    (candidate) =>
      assistantOperationKey(candidate) === assistantOperationKey(record) &&
      candidate.idempotencyKey === record.idempotencyKey,
  ) ?? null;

const update = (
  record: AssistantOperationRecord,
  changes: Partial<AssistantOperationRecordBase>,
): AssistantOperationRecord | null => {
  const stored = current(record);
  if (!stored) return null;
  const next = { ...stored, ...changes };
  writeAssistantOperation(next);
  return next;
};

const applyResult = (
  record: AssistantOperationRecord,
  result: AssistantOperationResult,
) =>
  result.status === 'pending'
    ? update(record, {
        operationId: result.operationId,
        retryAfterSeconds: result.retryAfterSeconds,
        state: 'pending',
        unansweredInPage: undefined,
      })
    : update(record, {
        operationId: result.operationId,
        outcome: {
          declineCode: result.declineCode,
          startAt: result.appointment?.startAt ?? null,
          status: result.status,
        },
        state: 'settled',
        unansweredInPage: undefined,
      });

type Step = 'continue' | 'stop';

const send = async (
  dispatch: AppDispatch,
  record: AssistantOperationRecord,
  onUnauthorized: () => void,
): Promise<Step> => {
  const request =
    record.action === 'book'
      ? dispatch(
          schedulingAssistantApi.endpoints.submitAssistantBook.initiate({
            memberId: record.memberId,
            requestBytes: record.requestBytes,
          }),
        )
      : dispatch(
          schedulingAssistantApi.endpoints.submitAssistantCommand.initiate({
            action: record.action,
            appointmentId: record.appointmentId,
            memberId: record.memberId,
            requestBytes: record.requestBytes,
          }),
        );
  const response = await request;
  request.reset();
  if ('data' in response && response.data) {
    return applyResult(record, response.data) ? 'continue' : 'stop';
  }
  const error = 'error' in response ? response.error : undefined;
  const status = assistantErrorStatus(error);
  if (status === 401) {
    onUnauthorized();
    return 'stop';
  }
  if (typeof status === 'number' && status >= 400 && status < 500) {
    // A definitive refusal: Moments answered, and nothing was applied.
    update(record, {
      outcome: { error: assistantErrorCode(error) ?? `http_${status}` },
      state: 'settled',
      unansweredInPage: undefined,
    });
    return 'stop';
  }
  // No answer, or a 5xx: the change may have gone through. Keep the bytes for
  // a replay with the same key, which can't apply it twice.
  update(record, { unansweredInPage: PAGE_LOAD_ID });
  return 'stop';
};

const poll = async (
  dispatch: AppDispatch,
  record: AssistantOperationRecord,
  failures: number,
  onUnauthorized: () => void,
  retired: () => boolean,
): Promise<{ failures: number; step: Step }> => {
  await sleep(pollDelay(record.retryAfterSeconds, failures));
  const stored = retired() ? null : current(record);
  if (!stored || stored.state !== 'pending' || !stored.operationId) {
    return { failures, step: 'stop' };
  }
  const response = await dispatch(
    schedulingAssistantApi.endpoints.getAssistantOperation.initiate(
      { memberId: stored.memberId, operationId: stored.operationId },
      { forceRefetch: true, subscribe: false },
    ),
  );
  if (response.data) {
    applyResult(stored, response.data);
    return { failures: 0, step: 'continue' };
  }
  const status = assistantErrorStatus(response.error);
  if (status === 401) {
    onUnauthorized();
    return { failures, step: 'stop' };
  }
  if (status === 403 || status === 404) {
    update(stored, {
      outcome: { error: assistantErrorCode(response.error) ?? 'not_found' },
      state: 'settled',
    });
    return { failures, step: 'stop' };
  }
  // Transient: the operation exists, so keep asking, more slowly.
  return { failures: failures + 1, step: 'continue' };
};

/**
 * Starts driving a record unless it is already being driven or is settled.
 * Safe to call on every mount: a reload replays a `submitting` record and
 * resumes polling a `pending` one.
 */
export const driveAssistantOperation = (
  dispatch: AppDispatch,
  record: AssistantOperationRecord,
  onUnauthorized: () => void,
): void => {
  const key = `${assistantOperationKey(record)}:${record.idempotencyKey}`;
  const startedIn = assistantOperationsGeneration();
  if (active.get(key) === startedIn || record.state === 'settled') return;
  active.set(key, startedIn);
  const retired = () => assistantOperationsGeneration() !== startedIn;

  const run = async () => {
    let failures = 0;
    let stored = current(record);
    while (stored && stored.state !== 'settled' && !retired()) {
      if (stored.state === 'submitting') {
        // eslint-disable-next-line no-await-in-loop -- one step at a time, by design
        if ((await send(dispatch, stored, onUnauthorized)) === 'stop') break;
      } else {
        // eslint-disable-next-line no-await-in-loop -- one step at a time, by design
        const polled = await poll(
          dispatch,
          stored,
          failures,
          onUnauthorized,
          retired,
        );
        failures = polled.failures;
        if (polled.step === 'stop') break;
      }
      stored = current(record);
    }
    const settled = retired() ? null : current(record);
    if (settled?.state === 'settled') {
      // The appointment changed, or was refused for being stale: re-read it.
      // A booking also moves the coach's next step.
      dispatch(
        schedulingAssistantApi.util.invalidateTags([
          'AssistantAppointment',
          'AssistantCoaches',
        ]),
      );
    }
  };

  run()
    .catch(() => {
      // A thrown error has no answer in it; the record stays for a replay.
      update(record, { unansweredInPage: PAGE_LOAD_ID });
    })
    .finally(() => {
      if (active.get(key) === startedIn) active.delete(key);
    });
};
